import { jsPDF } from "jspdf";
import * as XLSX from "xlsx";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { Message } from "primereact/message";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getItem } from "../../../../utils/localStorage";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import { fetchFacilityPaymentDetails, getContracts, generate_tenant_invoice, generate_facility_invoice, syncRetainerToZohoURL, checkFacilityZohoConfigURL } from "../../../../utils/urls";

import DisableContract from './disable_contract';
import EditContract from './edit_contract';
import TerminateContract from './terminate_contract';

const ActiveLeviesTab = () => {
    // ── All your existing state unchanged ────────────────────────────────────
    const navigate = useNavigate();
    const [contracts, setContracts] = useState([]);
    const [filteredContracts, setFilteredContracts] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedContractId, setSelectedContractId] = useState(null);
    const [filterType, setFilterType] = useState("active"); // default to active
    const [loading, setLoading] = useState(false);
    const [contractId, setContractId] = useState(null);
    const [contractName, setContractName] = useState("");
    const [amount, setAmount] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [paymentFrequency, setPaymentFrequency] = useState("");
    const [currency, setCurrency] = useState("");
    const [selectedContract, setSelectedContract] = useState(null);
    const [editDialogVisible, setEditDialogVisible] = useState(false);
    const [confirmAction, setConfirmAction] = useState("");
    const [selectedDisableStatus, setSelectedDisableStatus] = useState(false);
    const [isConfirmDialogVisible, setConfirmDialogVisible] = useState(false);
    const [terminateDialogVisible, setTerminateDialogVisible] = useState(false);
    const [selectedTerminateContract, setSelectedTerminateContract] = useState(null);
    const [generateInvoiceVisible, setGenerateInvoiceVisible] = useState(false);
    const [selectedContractForInvoice, setSelectedContractForInvoice] = useState(null);
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const [invoiceFormData, setInvoiceFormData] = useState({
        generateYearMonth: "", customDueDate: null, isNewTenant: false,
        prorate: false, prorateFromDate: null, sendNotification: true
    });
    const [availableMonths, setAvailableMonths] = useState([]);
    const [invoiceErrors, setInvoiceErrors] = useState({});
    const [runBillingVisible, setRunBillingVisible] = useState(false);
    const [billingLoading, setBillingLoading] = useState(false);
    const [selectedBillingMonth, setSelectedBillingMonth] = useState("");
    const [sendBillingNotification, setSendBillingNotification] = useState(true);
    const [billingError, setBillingError] = useState("");
    const [syncingContractId, setSyncingContractId] = useState(null);
    const [hasZohoConfig, setHasZohoConfig] = useState(false);

    // ── NEW: Export state ──────────────────────────────────────────────────
    const [exportDialogVisible, setExportDialogVisible] = useState(false);
    const [exportStartDateFrom, setExportStartDateFrom] = useState(null);
    const [exportStartDateTo, setExportStartDateTo] = useState(null);

    const fetchContracts = async () => {
        try {
            setLoading(true);
            const facilityId = await getItem("selectedFacilityId");
            if (!facilityId) { toastify("No facility selected.", "error"); return; }

            const response = await makeRequest2(`${getContracts}/${facilityId}`, "GET");

            console.log("Contracts response:", response);

            if (response?.success) {
                const contractsData = response.data?.contracts || response.data || [];

                const enriched = contractsData.map(c => ({
                    ...c,
                    formattedStartDate: c.startDate ? new Date(c.startDate).toISOString().split("T")[0] : '-',
                    formattedEndDate: c.endDate ? new Date(c.endDate).toISOString().split("T")[0] : '-'
                }));

                // ── Active tab: only Active status (Inactive/Terminated handled elsewhere) ──
                const activeOnly = enriched.filter(c => c.status === 'Active');

                setContracts(activeOnly);
                setFilteredContracts(activeOnly);
            } else {
                setContracts([]);
                setFilteredContracts([]);
            }
        } catch (error) {
            toastify("Error fetching contracts", "error");
            setContracts([]);
            setFilteredContracts([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchContracts(); }, []);

    // Check if facility has Zoho config
    useEffect(() => {
        const checkZoho = async () => {
            try {
                const facilityId = await getItem("selectedFacilityId");
                if (!facilityId) return;
                const res = await makeRequest2(`${checkFacilityZohoConfigURL(facilityId)}`, "GET");
                setHasZohoConfig(res?.success && !!res?.data?.data?.config);
            } catch {
                setHasZohoConfig(false);
            }
        };
        checkZoho();
    }, []);

    const handleSearchChange = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        setFilteredContracts(contracts.filter(c =>
            c.contractName?.toLowerCase().includes(term.toLowerCase()) ||
            c.customerName?.toLowerCase().includes(term.toLowerCase()) ||
            c.unitName?.toLowerCase().includes(term.toLowerCase())
        ));
    };

    // ── All your existing handlers unchanged ──────────────────────────────────
    const handleEdit = async (rowData) => {
        try {
            const facilityId = await getItem("selectedFacilityId");
            const res = await makeRequest2(`${fetchFacilityPaymentDetails}/${facilityId}`, "GET");
            if (res.success && res.data) {
                const paymentDetail = res.data.find(d => d._id === rowData.paymentMethodId);
                setContractId(rowData._id);
                setContractName(rowData.contractName);
                setAmount(rowData.amount);
                setStartDate(new Date(rowData.startDate).toISOString().split("T")[0]);
                setEndDate(new Date(rowData.endDate).toISOString().split("T")[0]);
                setPaymentFrequency(rowData.paymentFrequency);
                setCurrency(rowData.currency);
                setSelectedContract({ ...rowData, shortCode: paymentDetail?.shortCode || null });
                setEditDialogVisible(true);
            }
        } catch {
            setContractId(rowData._id);
            setContractName(rowData.contractName);
            setAmount(rowData.amount);
            setStartDate(new Date(rowData.startDate).toISOString().split("T")[0]);
            setEndDate(new Date(rowData.endDate).toISOString().split("T")[0]);
            setPaymentFrequency(rowData.paymentFrequency);
            setCurrency(rowData.currency);
            setSelectedContract(rowData);
            setEditDialogVisible(true);
        }
    };

    const handleTerminate = (rowData) => {
        setSelectedTerminateContract(rowData);
        setTerminateDialogVisible(true);
    };

    const handleClear = () => {
        setContractName(""); setAmount(""); setStartDate("");
        setEndDate(""); setPaymentFrequency(""); setCurrency(""); setSelectedContract(null);
    };

    const handleGenerateInvoice = (contract) => {
        setSelectedContractForInvoice(contract);
        generateAvailableMonths(contract);
        setInvoiceFormData({ generateYearMonth: "", customDueDate: null, isNewTenant: false, prorate: false, prorateFromDate: null, sendNotification: true });
        setInvoiceErrors({});
        setGenerateInvoiceVisible(true);
    };

    const generateAvailableMonths = (contractData) => {
    if (!contractData) return;
    const months = [];
    const currentDate = contractData.startDate ? new Date(contractData.startDate) : new Date();
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() + 1);
    while (currentDate <= maxDate) {
        const year = currentDate.getFullYear();
        const month = (currentDate.getMonth() + 1).toString().padStart(2, '0');
        months.push({
            label: currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
            value: `${year}-${month}`
        });
        currentDate.setMonth(currentDate.getMonth() + 1);
    }
    setAvailableMonths(months);
};

    const handleInvoiceInputChange = (field, value) => {
        setInvoiceFormData(prev => ({ ...prev, [field]: value }));
        if (invoiceErrors[field]) setInvoiceErrors(prev => ({ ...prev, [field]: null }));
    };

    const validateInvoiceForm = () => {
        const errors = {};
        if (!invoiceFormData.generateYearMonth) errors.generateYearMonth = "Please select a month";
        if (invoiceFormData.prorate && !invoiceFormData.prorateFromDate) errors.prorateFromDate = "Please select a proration start date";
        setInvoiceErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleInvoiceSubmit = async () => {
        if (!validateInvoiceForm()) return;
        try {
            setInvoiceLoading(true);
            const facilityId = await getItem("selectedFacilityId");
            const payload = {
                facilityId,
                contractId: selectedContractForInvoice._id,
                contractType: "levy",
                options: {
                    generateYearMonth: invoiceFormData.generateYearMonth,
                    ...(invoiceFormData.customDueDate && { customDueDate: invoiceFormData.customDueDate.toISOString() }),
                    ...(invoiceFormData.isNewTenant && { isNewTenant: true }),
                    ...(invoiceFormData.prorate && invoiceFormData.prorateFromDate && { prorate: true, prorateFromDate: invoiceFormData.prorateFromDate.toISOString() }),
                    sendNotification: invoiceFormData.sendNotification
                }
            };
            const response = await makeRequest2(`${generate_tenant_invoice}`, "POST", payload);
            if (response.success) {
                toastify("Invoice generated successfully", "success");
                setGenerateInvoiceVisible(false);
                fetchContracts();
            } else throw new Error(response.message || 'Failed');
        } catch (error) {
            toastify(error.message || 'Error generating invoice', "error");
        } finally {
            setInvoiceLoading(false);
        }
    };

    const generateBillingMonths = () => {
        const months = [];
        const now = new Date();
        for (let i = -12; i <= 12; i++) {
            const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
            const year = d.getFullYear();
            const month = (d.getMonth() + 1).toString().padStart(2, '0');
            months.push({ label: d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }), value: `${year}-${month}` });
        }
        return months;
    };

    const handleRunBilling = () => { setSelectedBillingMonth(""); setSendBillingNotification(true); setBillingError(""); setRunBillingVisible(true); };
    const handleBillingCancel = () => { setSelectedBillingMonth(""); setSendBillingNotification(true); setBillingError(""); setRunBillingVisible(false); };

    const handleBillingSubmit = async () => {
        if (!selectedBillingMonth) { setBillingError("Please select a month"); return; }
        setBillingLoading(true);
        try {
            const facilityId = await getItem("selectedFacilityId");
            const response = await makeRequest2(`${generate_facility_invoice}/${facilityId}`, "POST", {
                invoiceTypes: ["LEVY"], priority: 8, delay: 0,
                generateYearMonth: selectedBillingMonth, sendNotification: sendBillingNotification
            });
            if (response.success || response.message) {
                toastify('Facility billing triggered successfully!', "success");
                setRunBillingVisible(false);
                fetchContracts();
            } else throw new Error(response.error || 'Failed');
        } catch (error) {
            toastify(error.message || 'Error triggering billing', "error");
        } finally {
            setBillingLoading(false);
        }
    };

    const getMinDate = () => {
        if (!invoiceFormData.generateYearMonth) return new Date();
        const [y, m] = invoiceFormData.generateYearMonth.split('-').map(Number);
        return new Date(y, m - 1, 1);
    };
    const getMaxDate = () => {
        if (!invoiceFormData.generateYearMonth) return new Date();
        const [y, m] = invoiceFormData.generateYearMonth.split('-').map(Number);
        return new Date(y, m, 0);
    };

    // ── NEW: Export helpers ─────────────────────────────────────────────────

    // Applies the start-date range filter (if set) to the active contracts list
    const getExportRows = () => {
        return contracts.filter(c => {
            if (!exportStartDateFrom && !exportStartDateTo) return true;
            if (!c.startDate) return false;
            const sd = new Date(c.startDate);
            if (exportStartDateFrom && sd < exportStartDateFrom) return false;
            if (exportStartDateTo && sd > exportStartDateTo) return false;
            return true;
        });
    };

    // Groups the filtered rows by levy type, producing per-type count + total amount
    const getExportSummary = (rows) => {
        const byType = {};
        rows.forEach(c => {
            const type = c.levyType || c.levyName || "Uncategorized";
            if (!byType[type]) byType[type] = { levyType: type, count: 0, total: 0 };
            byType[type].count += 1;
            byType[type].total += Number(c.amount) || 0;
        });
        return Object.values(byType).sort((a, b) => b.total - a.total);
    };

    const exportRows = getExportRows();
    const exportSummary = getExportSummary(exportRows);
    const exportGrandTotal = exportRows.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);

    const clearExportFilters = () => {
        setExportStartDateFrom(null);
        setExportStartDateTo(null);
    };

    const handleOpenExport = () => {
        clearExportFilters();
        setExportDialogVisible(true);
    };

    const handleExportExcel = () => {
        const rows = getExportRows();
        if (rows.length === 0) {
            toastify("No contracts match the selected filter.", "warn");
            return;
        }
        const summary = getExportSummary(rows);
        const grandTotal = rows.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);

        // Summary sheet
        const summarySheetData = [
            ["Active Levy Contracts - Summary"],
            [`Generated: ${new Date().toLocaleString()}`],
            [`Total Contracts: ${rows.length}`],
            [`Total Amount: ${grandTotal.toLocaleString()}`],
            [],
            ["Levy Type", "Count", "Total Amount"],
            ...summary.map(s => [s.levyType, s.count, s.total]),
        ];
        const summarySheet = XLSX.utils.aoa_to_sheet(summarySheetData);

        // Detail sheet
        const detailSheetData = [
            ["Unit", "Customer Name", "Levy Type", "Amount", "Start Date"],
            ...rows.map(c => [
                c.unitName || "-",
                c.customerName || "-",
                c.levyType || c.levyName || "-",
                Number(c.amount) || 0,
                c.startDate ? new Date(c.startDate).toISOString().split("T")[0] : "-",
            ]),
        ];
        const detailSheet = XLSX.utils.aoa_to_sheet(detailSheetData);

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, summarySheet, "Summary");
        XLSX.utils.book_append_sheet(wb, detailSheet, "Contracts");

        XLSX.writeFile(wb, `active_levy_contracts_${new Date().toISOString().split("T")[0]}.xlsx`);
        toastify("Excel export downloaded", "success");
    };

    const handleExportPDF = () => {
        const rows = getExportRows();
        if (rows.length === 0) {
            toastify("No contracts match the selected filter.", "warn");
            return;
        }
        const summary = getExportSummary(rows);
        const grandTotal = rows.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);

        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;
        const margin = 14;
        let y = 20;

        doc.setFillColor(0, 51, 102);
        doc.rect(0, 0, pageWidth, 30, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text("ACTIVE LEVY CONTRACTS REPORT", pageWidth / 2, 18, { align: "center" });
        doc.setTextColor(0, 0, 0);
        y = 40;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`Generated: ${new Date().toLocaleString()}`, margin, y); y += 6;
        if (exportStartDateFrom || exportStartDateTo) {
            const fromStr = exportStartDateFrom ? exportStartDateFrom.toLocaleDateString() : "Any";
            const toStr = exportStartDateTo ? exportStartDateTo.toLocaleDateString() : "Any";
            doc.text(`Start Date Filter: ${fromStr} - ${toStr}`, margin, y); y += 6;
        }
        doc.setFont('helvetica', 'bold');
        doc.text(`Total Contracts: ${rows.length}    Total Amount: ${grandTotal.toLocaleString()}`, margin, y);
        y += 10;

        // Summary by type
        doc.setFontSize(12);
        doc.text("Summary by Levy Type", margin, y); y += 2;
        doc.setDrawColor(0, 51, 102);
        doc.line(margin, y, pageWidth - margin, y); y += 6;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        summary.forEach(s => {
            doc.text(`${s.levyType}`, margin, y);
            doc.text(`${s.count} contracts`, margin + 90, y);
            doc.text(`${s.total.toLocaleString()}`, margin + 140, y);
            y += 6;
            if (y > pageHeight - 30) { doc.addPage(); y = 20; }
        });
        y += 6;

        // Detail table (manual, matching your existing style — no autotable dependency)
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text("Contract Details", margin, y); y += 2;
        doc.line(margin, y, pageWidth - margin, y); y += 8;

        const colX = { unit: margin, customer: margin + 30, amount: margin + 110, start: margin + 145 };
        doc.setFontSize(9);
        doc.text("Unit", colX.unit, y);
        doc.text("Customer", colX.customer, y);
        doc.text("Amount", colX.amount, y);
        doc.text("Start Date", colX.start, y);
        y += 5;
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, y, pageWidth - margin, y);
        y += 5;

        doc.setFont('helvetica', 'normal');
        rows.forEach(c => {
            if (y > pageHeight - 20) { doc.addPage(); y = 20; }
            doc.text(String(c.unitName || "-"), colX.unit, y);
            doc.text(String(c.customerName || "-").substring(0, 30), colX.customer, y);
            doc.text(String((Number(c.amount) || 0).toLocaleString()), colX.amount, y);
            doc.text(c.startDate ? new Date(c.startDate).toISOString().split("T")[0] : "-", colX.start, y);
            y += 6;
        });

        doc.save(`active_levy_contracts_${new Date().toISOString().split("T")[0]}.pdf`);
        toastify("PDF export downloaded", "success");
    };

    // ── Templates ─────────────────────────────────────────────────────────────
    const dateTemplate = (rowData, field) => {
        if (!rowData[field]) return '-';
        try { return new Date(rowData[field]).toISOString().split("T")[0]; } catch { return '-'; }
    };

    const statusTemplate = (rowData) => {
        return (
            <span style={{ fontWeight: "bold", color: "#4caf50", padding: "5px 10px", borderRadius: "15px", backgroundColor: "#e8f5e9" }}>
                Active
            </span>
        );
    };

    const amountTemplate = (rowData) =>
        `${rowData.currencyCode || ''} ${rowData.amount?.toLocaleString() || '0'}`;

    const zohoSyncTemplate = (rowData) => {
        if (!hasZohoConfig) return <span style={{ color: "#9e9e9e", fontSize: "0.82rem" }}>—</span>;
        const syncStatus = rowData.zohoSync?.retainerSyncStatus || "not-synced";
        const cfg = {
            synced: { color: "#4caf50", bg: "#e8f5e9", icon: "ti ti-circle-check", label: "Synced" },
            failed: { color: "#f44336", bg: "#ffebee", icon: "ti ti-alert-circle", label: "Failed" },
            "not-synced": { color: "#ff9800", bg: "#fff3e0", icon: "ti ti-cloud-upload", label: "Not Synced" },
            "not-applicable": { color: "#9e9e9e", bg: "#f5f5f5", icon: "ti ti-minus", label: "N/A" },
        }[syncStatus] || { color: "#9e9e9e", bg: "#f5f5f5", icon: "ti ti-minus", label: "N/A" };
        return (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontWeight: "600", color: cfg.color, padding: "4px 10px", borderRadius: "15px", backgroundColor: cfg.bg, fontSize: "0.82rem" }}>
                <i className={cfg.icon} />
                {cfg.label}
            </span>
        );
    };

    const handleSyncRetainerToZoho = async (rowData) => {
        const facilityId = await getItem("selectedFacilityId");
        if (!facilityId) { toastify("No facility selected.", "error"); return; }
        if (!rowData.zohoSync?.retainerInvoiceId && rowData.zohoSync?.retainerSyncStatus === "synced") {
            toastify("Retainer invoice already synced.", "info");
            return;
        }
        setSyncingContractId(rowData._id);
        try {
            const res = await makeRequest2(syncRetainerToZohoURL(facilityId, rowData._id), "POST");
            const apiData = res?.data?.data || res?.data;
            if (res?.success && apiData?.success) {
                toastify(apiData.message || "Retainer invoice synced to Zoho Books", "success");
                fetchContracts();
            } else {
                toastify(apiData?.error || apiData?.message || res?.error || "Failed to sync retainer to Zoho", "error");
            }
        } catch (err) {
            toastify(err.message || "Failed to sync retainer to Zoho", "error");
        } finally {
            setSyncingContractId(null);
        }
    };

    const actionBodyTemplate = (rowData) => (
        <div className="actions d-flex flex-nowrap justify-content-center" style={{ gap: '0.25rem', minWidth: '200px' }}>
            <Button
                icon="ti ti-edit"
                className="p-button-rounded p-button-info p-button-sm"
                onClick={() => handleEdit(rowData)}
                tooltip="Edit"
                style={{ width: '2.5rem', height: '2.5rem' }}
            />
            <Button
                icon="ti ti-lock"
                className="p-button-rounded p-button-warning p-button-sm"
                onClick={() => {
                    setSelectedContractId(rowData._id);
                    setConfirmAction("deactivate");
                    setSelectedDisableStatus(false);
                    setConfirmDialogVisible(true);
                }}
                tooltip="Deactivate"
                style={{ width: '2.5rem', height: '2.5rem' }}
            />
            <Button
                icon="ti ti-x"
                className="p-button-rounded p-button-danger p-button-sm"
                onClick={() => handleTerminate(rowData)}
                tooltip="Terminate Contract"
                style={{ width: '2.5rem', height: '2.5rem' }}
            />
            <Button
                icon="ti ti-receipt"
                className="p-button-rounded p-button-success p-button-sm"
                onClick={() => handleGenerateInvoice(rowData)}
                tooltip="Generate Invoice"
                style={{ width: '2.5rem', height: '2.5rem' }}
            />
            <Button
                icon="ti ti-download"
                className="p-button-rounded p-button-primary p-button-sm"
                onClick={() => downloadContract(rowData)}
                tooltip="Download Contract"
                style={{ width: '2.5rem', height: '2.5rem' }}
            />
            {hasZohoConfig && rowData.zohoSync?.retainerSyncStatus !== "synced" && (
                <Button
                    icon={syncingContractId === rowData._id ? "ti ti-loader" : "ti ti-cloud-upload"}
                    className="p-button-rounded p-button-help p-button-sm"
                    onClick={() => handleSyncRetainerToZoho(rowData)}
                    tooltip="Sync Retainer to Zoho"
                    loading={syncingContractId === rowData._id}
                    disabled={syncingContractId === rowData._id}
                    style={{ width: '2.5rem', height: '2.5rem' }}
                />
            )}
        </div>
    );

    const downloadContract = async (contract) => {
        // ── Your existing downloadContract function unchanged ─────────────────
        const facilityId = await getItem("selectedFacilityId");
        if (!facilityId) { toastify("No facility selected.", "error"); return; }
        try {
            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.width;
            const pageHeight = doc.internal.pageSize.height;
            const margin = 20;
            doc.setFillColor(0, 51, 102);
            doc.rect(0, 0, pageWidth, 40, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(22);
            doc.setFont('helvetica', 'bold');
            doc.text("LEVY CONTRACT AGREEMENT", pageWidth / 2, 25, { align: "center" });
            doc.setFontSize(10);
            doc.text(`Contract Reference: ${contract._id}`, pageWidth / 2, 35, { align: "center" });
            doc.setTextColor(0, 0, 0);
            let y = 50;
            const section = (title) => {
                doc.setFontSize(16); doc.setFont("helvetica", "bold"); doc.text(title, margin, y); y += 2;
                doc.setDrawColor(0, 51, 102); doc.setLineWidth(0.5); doc.line(margin, y, pageWidth - margin, y); y += 10;
                doc.setFont("helvetica", "normal"); doc.setFontSize(11);
            };
            const row = (label, value) => {
                doc.setFont("helvetica", "bold"); doc.text(label, margin, y);
                doc.setFont("helvetica", "normal"); doc.text(String(value || "-"), margin + 60, y); y += 7;
            };
            section("Contract Information");
            row("Contract Name:", contract.contractName);
            row("Start Date:", new Date(contract.startDate).toLocaleDateString());
            row("End Date:", new Date(contract.endDate).toLocaleDateString());
            row("Status:", contract.status);
            y += 5;
            section("Levy Information");
            row("Levy Name:", contract.levyName);
            row("Levy Type:", contract.levyType);
            row("Applicant Type:", contract.levyApplicant);
            row("Amount:", `${contract.currencyCode || ''} ${contract.amount?.toLocaleString()}`);
            row("Payment Frequency:", contract.paymentFrequency);
            y += 5;
            section("Customer Information");
            row("Customer Name:", contract.customerName);
            row("Customer Type:", contract.customerType);
            row("Unit:", contract.unitName);
            row("Unit Location:", contract.division && contract.floorUnitNo ? `${contract.division}, ${contract.floorUnitNo}` : "-");
            y = pageHeight - 80;
            doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.text("Signatures", margin, y); y += 2;
            doc.setDrawColor(0, 51, 102); doc.setLineWidth(0.5); doc.line(margin, y, pageWidth - margin, y); y += 10;
            doc.setDrawColor(0, 0, 0); doc.setLineWidth(0.2);
            doc.line(margin, y, margin + 70, y); doc.line(pageWidth - margin - 70, y, pageWidth - margin, y);
            doc.setFontSize(10); doc.setFont("helvetica", "normal");
            doc.text("Customer Signature", margin, y + 5); doc.text("Facility Representative", pageWidth - margin - 70, y + 5);
            doc.text("Date: ________________", margin, y + 15); doc.text("Date: ________________", pageWidth - margin - 70, y + 15);
            doc.setFontSize(8); doc.setTextColor(100, 100, 100);
            doc.text("This document is electronically generated.", pageWidth / 2, pageHeight - 15, { align: "center" });
            doc.text(`Generated on ${new Date().toLocaleDateString()}`, pageWidth / 2, pageHeight - 10, { align: "center" });
            doc.save(`${(contract.contractName || 'contract').replace(/[^a-zA-Z0-9]/g, "_")}_${contract._id.substring(0, 8)}.pdf`);
            toastify("Contract downloaded successfully", "success");
        } catch (error) {
            toastify("Error generating contract PDF: " + (error.message || "Unknown error"), "error");
        }
    };

    return (
        <div>
            {/* ── Toolbar ── */}
            <div className="row mb-3">
                <div className="col-md-3">
                    <input
                        className="form-control"
                        placeholder="Search here"
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                </div>
                <div className="col-md-9 d-flex justify-content-end gap-2">
                    <button
                        className="btn btn-outline-primary"
                        onClick={handleOpenExport}
                    >
                        Export
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleRunBilling}
                    >
                        Run Billing
                    </button>
                    <button
                        className="btn btn-success"
                        onClick={() => navigate("/facility/levy_management/contracts/add_contract")}
                    >
                        Add Details
                    </button>
                </div>
            </div>

            {/* ── Table ── */}
            <DataTable
                value={filteredContracts}
                emptyMessage="No active levy details found."
                paginator rows={10}
                stripedRows
                loading={loading}
                tableStyle={{ minWidth: "70rem" }}
                sortMode="multiple"
                removableSort
            >
                <Column field="contractName" header="Contract Name" sortable style={{ minWidth: '200px' }} />
                <Column field="customerName" header="Customer" body={(r) => r.customerName || '-'} sortable style={{ minWidth: '150px' }} />
                <Column field="unitName" header="Unit" body={(r) => r.unitName || '-'} sortable style={{ minWidth: '120px' }} />
                <Column field="levyName" header="Levy" body={(r) => r.levyName || '-'} sortable style={{ minWidth: '150px' }} />
                <Column field="amount" header="Amount" body={amountTemplate} sortable style={{ minWidth: '120px' }} />
                <Column field="startDate" header="Start Date" sortable body={(r) => dateTemplate(r, "startDate")} style={{ minWidth: '120px' }} />
                <Column field="endDate" header="End Date" sortable body={(r) => dateTemplate(r, "endDate")} style={{ minWidth: '120px' }} />
                <Column field="status" header="Status" body={statusTemplate} sortable style={{ minWidth: '100px' }} />
                {hasZohoConfig && <Column header="Zoho Sync" body={zohoSyncTemplate} style={{ minWidth: '120px' }} />}
                <Column body={actionBodyTemplate} header="Actions" style={{ minWidth: '260px' }} />
            </DataTable>

            {/* ── Modals (all unchanged) ── */}
            <EditContract
                visible={editDialogVisible}
                onHide={() => setEditDialogVisible(false)}
                contractId={contractId} contractName={contractName} setContractName={setContractName}
                amount={amount} setAmount={setAmount} startDate={startDate} setStartDate={setStartDate}
                endDate={endDate} setEndDate={setEndDate} paymentFrequency={paymentFrequency}
                setPaymentFrequency={setPaymentFrequency} currency={currency} setCurrency={setCurrency}
                handleClear={handleClear} onSuccess={fetchContracts} initialContract={selectedContract}
            />
            {confirmAction === "deactivate" && (
                <DisableContract
                    visible={isConfirmDialogVisible}
                    onHide={() => setConfirmDialogVisible(false)}
                    contractId={selectedContractId}
                    isActivating={false}
                    onSuccess={fetchContracts}
                />
            )}
            <TerminateContract
                visible={terminateDialogVisible}
                onHide={() => setTerminateDialogVisible(false)}
                contractId={selectedTerminateContract?._id}
                contractName={selectedTerminateContract?.contractName}
                onSuccess={fetchContracts}
            />

            {/* Generate Invoice Dialog */}
            <Dialog
                visible={generateInvoiceVisible}
                onHide={() => setGenerateInvoiceVisible(false)}
                header="Generate Levy Invoice"
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancel" className="btn btn-outline-secondary" onClick={() => setGenerateInvoiceVisible(false)} disabled={invoiceLoading} />
                        <Button label="Generate Invoice" className="btn btn-primary" onClick={handleInvoiceSubmit} loading={invoiceLoading} disabled={!invoiceFormData.generateYearMonth} />
                    </div>
                }
                style={{ width: '500px' }} modal
            >
                <div className="grid p-fluid">
                    <div className="col-12 mb-3">
                        <div className="p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                            <h6 className="mb-2">Contract Information</h6>
                            <div className="grid">
                                <div className="col-6"><strong>Customer:</strong> {selectedContractForInvoice?.customerName || 'N/A'}</div>
                                <div className="col-6"><strong>Unit:</strong> {selectedContractForInvoice?.unitName || 'N/A'}</div>
                                <div className="col-6"><strong>Levy:</strong> {selectedContractForInvoice?.levyName || 'N/A'}</div>
                                <div className="col-6"><strong>Amount:</strong> {selectedContractForInvoice?.currencyCode} {selectedContractForInvoice?.amount?.toLocaleString()}</div>
                                <div className="col-6"><strong>Frequency:</strong> {selectedContractForInvoice?.paymentFrequency || 'N/A'}</div>
                                <div className="col-6"><strong>Last Invoice:</strong> {selectedContractForInvoice?.lastInvoiceYearMonth || 'None'}</div>
                            </div>
                        </div>
                    </div>
                    <div className="col-12">
                        <label className="font-bold">Invoice Month <span className="text-red-500">*</span></label>
                        <Dropdown value={invoiceFormData.generateYearMonth} options={availableMonths} onChange={(e) => handleInvoiceInputChange('generateYearMonth', e.value)} placeholder="Select month" className={invoiceErrors.generateYearMonth ? 'p-invalid' : ''} />
                        {invoiceErrors.generateYearMonth && <small className="p-error">{invoiceErrors.generateYearMonth}</small>}
                    </div>
                    <div className="col-12">
                        <label className="font-bold">Custom Due Date (Optional)</label>
                        <Calendar value={invoiceFormData.customDueDate} onChange={(e) => handleInvoiceInputChange('customDueDate', e.value)} placeholder="Select custom due date" showIcon dateFormat="mm/dd/yy" />
                    </div>
                    <div className="col-12">
                        <div className="flex align-items-center">
                            <InputSwitch checked={invoiceFormData.isNewTenant} onChange={(e) => handleInvoiceInputChange('isNewTenant', e.value)} />
                            <label className="ml-2 font-bold">New Customer</label>
                        </div>
                    </div>
                    <div className="col-12">
                        <div className="flex align-items-center mb-2">
                            <InputSwitch checked={invoiceFormData.prorate} onChange={(e) => handleInvoiceInputChange('prorate', e.value)} />
                            <label className="ml-2 font-bold">Prorate Invoice</label>
                        </div>
                    </div>
                    {invoiceFormData.prorate && (
                        <div className="col-12">
                            <label className="font-bold">Proration Start Date <span className="text-red-500">*</span></label>
                            <Calendar value={invoiceFormData.prorateFromDate} onChange={(e) => handleInvoiceInputChange('prorateFromDate', e.value)} showIcon dateFormat="mm/dd/yy" minDate={getMinDate()} maxDate={getMaxDate()} className={invoiceErrors.prorateFromDate ? 'p-invalid' : ''} />
                            {invoiceErrors.prorateFromDate && <small className="p-error">{invoiceErrors.prorateFromDate}</small>}
                        </div>
                    )}
                    <div className="col-12">
                        <div className="flex align-items-center">
                            <InputSwitch checked={invoiceFormData.sendNotification} onChange={(e) => handleInvoiceInputChange('sendNotification', e.value)} />
                            <label className="ml-2 font-bold">Send Notification</label>
                        </div>
                    </div>
                    {availableMonths.length === 0 && (
                        <div className="col-12">
                            <Message severity="warn" text="No available months for invoice generation." />
                        </div>
                    )}
                </div>
            </Dialog>

            {/* Run Billing Dialog */}
            <Dialog
                visible={runBillingVisible}
                onHide={handleBillingCancel}
                header="Run Facility Billing"
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancel" className="btn btn-outline-secondary" onClick={handleBillingCancel} disabled={billingLoading} />
                        <Button label="Run Billing" className="btn btn-primary" onClick={handleBillingSubmit} loading={billingLoading} disabled={!selectedBillingMonth} />
                    </div>
                }
                style={{ width: '500px' }} modal
            >
                <div className="grid p-fluid">
                    <div className="col-12 mb-3">
                        <Message severity="info" text="Generate invoices for all active levy contracts in this facility for the selected month." style={{ width: '100%' }} />
                    </div>
                    <div className="col-12">
                        <label className="font-bold mb-2">Billing Month <span style={{ color: 'red' }}>*</span></label>
                        <Dropdown value={selectedBillingMonth} options={generateBillingMonths()} onChange={(e) => { setSelectedBillingMonth(e.value); setBillingError(""); }} placeholder="Select month" className={billingError ? 'p-invalid' : ''} />
                        {billingError && <small style={{ color: 'red' }}>{billingError}</small>}
                    </div>
                    <div className="col-12 mt-3">
                        <div className="flex align-items-center">
                            <InputSwitch checked={sendBillingNotification} onChange={(e) => setSendBillingNotification(e.value)} />
                            <label className="ml-2 font-bold">Send Notifications</label>
                        </div>
                    </div>
                </div>
            </Dialog>

            {/* ── NEW: Export Dialog ── */}
            <Dialog
                visible={exportDialogVisible}
                onHide={() => setExportDialogVisible(false)}
                header="Export Active Levy Contracts"
                style={{ width: '900px', maxWidth: '95vw' }}
                modal
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Close" className="btn btn-outline-secondary" onClick={() => setExportDialogVisible(false)} />
                        <Button label="Export to Excel" icon="ti ti-file-spreadsheet" className="btn btn-success" onClick={handleExportExcel} />
                        <Button label="Export to PDF" icon="ti ti-file-type-pdf" className="btn btn-primary" onClick={handleExportPDF} />
                    </div>
                }
            >
                <div className="grid p-fluid mb-3">
                    <div className="col-6">
                        <label className="font-bold">Start Date From</label>
                        <Calendar
                            value={exportStartDateFrom}
                            onChange={(e) => setExportStartDateFrom(e.value)}
                            showIcon dateFormat="mm/dd/yy"
                            placeholder="Any"
                        />
                    </div>
                    <div className="col-6">
                        <label className="font-bold">Start Date To</label>
                        <Calendar
                            value={exportStartDateTo}
                            onChange={(e) => setExportStartDateTo(e.value)}
                            showIcon dateFormat="mm/dd/yy"
                            placeholder="Any"
                        />
                    </div>
                    {(exportStartDateFrom || exportStartDateTo) && (
                        <div className="col-12">
                            <Button label="Clear Filter" className="p-button-text p-button-sm" onClick={clearExportFilters} />
                        </div>
                    )}
                </div>

                {/* Summary */}
                <div className="p-3 mb-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                    <div className="grid">
                        <div className="col-4"><strong>Total Contracts:</strong> {exportRows.length}</div>
                        <div className="col-4"><strong>Total Amount:</strong> {exportGrandTotal.toLocaleString()}</div>
                        <div className="col-4"><strong>Levy Types:</strong> {exportSummary.length}</div>
                    </div>
                    {exportSummary.length > 0 && (
                        <div className="mt-3">
                            <DataTable value={exportSummary} size="small" showGridlines>
                                <Column field="levyType" header="Levy Type" />
                                <Column field="count" header="Contracts" />
                                <Column field="total" header="Total Amount" body={(r) => r.total.toLocaleString()} />
                            </DataTable>
                        </div>
                    )}
                </div>

                {/* Preview */}
                <h6 className="mb-2">Preview ({exportRows.length} contracts)</h6>
                <DataTable
                    value={exportRows}
                    paginator rows={5}
                    size="small"
                    emptyMessage="No contracts match this filter."
                    tableStyle={{ minWidth: '100%' }}
                >
                    <Column field="unitName" header="Unit" body={(r) => r.unitName || '-'} />
                    <Column field="customerName" header="Customer Name" body={(r) => r.customerName || '-'} />
                    <Column field="amount" header="Amount" body={amountTemplate} />
                    <Column field="startDate" header="Start Date" body={(r) => dateTemplate(r, "startDate")} />
                </DataTable>
            </Dialog>
        </div>
    );
};

export default ActiveLeviesTab;